import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Stack, IconButton, Tooltip, Box, Button } from '@mui/material';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import Alert from './Alert.component';
import DrawerComponent from './Drawer.component';
import { Form } from '../form/Form';
import { fetchAPI } from '../../api/request';
import { InfoOutlined } from '@mui/icons-material';
import _ from 'lodash';

const DrawerSendEmail = ({
  id,
  disabled = false,
  conf,
  data,
  tag,
  sendEmailApi,
  idKey,
  tooltip,
  onOpen,
  transformSubmitValues,
  showMailSendedFeedback = false,
}) => {
  const { jwt } = useSelector((state) => state.login);
  const userAuth = useSelector((state) => state.login?.user);

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mailSendedLocal, setMailSendedLocal] = useState(false);
  const { fields, schema, defaultValues } = conf;

  const defaultValue = useMemo(
    () => ({
      ...defaultValues,
      ...data,
    }),
    [data, defaultValues]
  );
  const hasPermissions = useMemo(
    () => userAuth.permissions[_.toUpper(tag)]?.UPDATE === 'true',
    [userAuth, tag]
  );
  const mailSended = useMemo(() => {
    const rawMailSended = _.get(
      data,
      'attributes.mail_sended',
      _.get(data, 'mail_sended')
    );

    if (typeof rawMailSended === 'string') {
      return rawMailSended.toLowerCase() === 'true';
    }

    return rawMailSended === true;
  }, [data]);

  const onSuccess = useCallback(() => {
    setSuccess(true);
    if (showMailSendedFeedback) setMailSendedLocal(true);
    setLoading(false);
    // eslint-disable-next-line
  }, [setLoading, setSuccess, showMailSendedFeedback]);

  const onError = useCallback((error) => {
    const moreDetails = error?.response?.data?.error?.details?.moreDetails;
    const apiErrorMessage = error?.response?.data?.error?.message;
    setError({ message: moreDetails || apiErrorMessage || error?.message });
    setLoading(false);
    // eslint-disable-next-line
  }, []);

  const onSubmit = useCallback(
    (values) => {
      setError(null);
      setSuccess(false);
      setLoading(true);

      const dataForm = {
        ...values,
        [idKey]: id,
        email: values.email || '',
        sendMail: true,
        mail_sended: true,
        id_user: userAuth?.id || null,
        ...(transformSubmitValues ? transformSubmitValues(values) : {}),
      };
      fetchAPI(sendEmailApi, {}, '', { data: dataForm }, jwt)
        .then(onSuccess)
        .catch(onError);
    },
    [setLoading, jwt, onError, onSuccess, id, idKey, sendEmailApi, transformSubmitValues, userAuth?.id]
  );

  return (
    <Fragment>
      {success && <Alert severity='success'>{'Success: Email sent!'}</Alert>}
      {error && (
        <Alert severity='error'>
          {error?.message ? error.message : 'An error has occurred'}
        </Alert>
      )}
      {!!hasPermissions && (
        <DrawerComponent
          button={
            <Stack direction={'row'} alignItems={'center'}>
              <Tooltip title='Send Email'>
                <IconButton color='primary' size='small' disabled={disabled}>
                  <ForwardToInboxIcon />
                </IconButton>
              </Tooltip>
              {showMailSendedFeedback && (mailSended || mailSendedLocal) && (
                <Tooltip title='Email sent at least once'>
                  <PriorityHighIcon sx={{ color: 'red' }} />
                </Tooltip>
              )}
            </Stack>
          }
          onOpen={onOpen}
        >
          <Stack spacing={2} width={1}>
            <Form
              fields={fields}
              schema={schema}
              defaultValues={defaultValue}
              onSubmit={onSubmit}
            >
              <Box
                display='flex'
                justifyContent={'right'}
                alignItems={'center'}
              >
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                  <Tooltip title={tooltip}>
                    <InfoOutlined />
                  </Tooltip>
                  <Button
                    type='submit'
                    variant='contained'
                    disabled={loading}
                    autoFocus
                  >
                    {loading ? 'Sending...' : 'Send Email'}
                  </Button>
                </Stack>
              </Box>
            </Form>
          </Stack>
        </DrawerComponent>
      )}
    </Fragment>
  );
};

export default DrawerSendEmail;
